/**
 * /whereami client — IP→country(+region) lookup for the branded iframe.
 *
 * Why this exists
 * ---------------
 * `allstar-partner-api`'s playlist endpoint is response-cached upstream
 * keyed on path + query string. To get a geo-targeted ad campaign without
 * cross-contaminating viewers, the playlist URL must include an explicit
 * `&geo=XX` so the cache partitions by country. This helper asks the
 * uncached `ex-lambdas` endpoint to resolve the caller's IP to a country
 * code before we build the playlist URL.
 *
 * `region` (bare ISO 3166-2 subdivision suffix, e.g. "WA" for a US IP) is
 * forwarded to the playlist URL as `&region=` — but by the caller for US
 * viewers ONLY. State-level campaign exclusion (adCampaign excludeRegions)
 * is a US-market feature, and `region` is a cache-key param: sending it
 * for every country would fragment the playlist response cache ~50x per
 * geo for no consumer.
 *
 * Failure semantics
 * -----------------
 * Returns `{ country: null, region: null }` for every failure mode —
 * network error, non-2xx, malformed body, missing env var, aborted
 * request. The caller treats a null country as "no geo override" and
 * omits `&geo=` from the playlist URL, which after partner-api PR #1054
 * means partner-api will omit `adCampaign` entirely. That degrades
 * cleanly (unbranded playlist) instead of risking the cache-contamination
 * bug we are trying to escape. A null region with a resolved country is
 * normal (subdivision unknown) — partner-api then fails closed on
 * exclude-gated campaigns only.
 */

export interface WhereamiResult {
  /** ISO 3166-1 alpha-2 country code, or null when unresolvable. */
  country: string | null;
  /** Bare ISO 3166-2 subdivision suffix ("WA"), or null when unknown. */
  region: string | null;
}

const UNRESOLVED: WhereamiResult = { country: null, region: null };

/**
 * Fetch the caller's country + subdivision from the ex-lambdas /whereami
 * endpoint.
 *
 * @param signal AbortSignal — pass the playlist fetch's controller so
 *               unmount cancels both legs together.
 */
export const resolveGeoFromWhereami = async (
  signal?: AbortSignal,
): Promise<WhereamiResult> => {
  const url = process.env.REACT_APP_WHEREAMI_URL;
  if (!url) return UNRESOLVED;
  try {
    const res = await fetch(url, {
      method: "GET",
      // Don't send cookies — this endpoint doesn't read them and CORS
      // for cookie-bearing requests would require credentials handling
      // on the lambda side.
      credentials: "omit",
      signal,
    });
    if (!res.ok) return UNRESOLVED;
    const data: unknown = await res.json();
    if (!data || typeof data !== "object") return UNRESOLVED;
    const { country, region } = data as {
      country?: unknown;
      region?: unknown;
    };
    if (typeof country !== "string" || country.length === 0) return UNRESOLVED;
    return {
      country: country.toUpperCase(),
      // Region without country is meaningless, so it only surfaces on the
      // resolved path. Older whereami deploys omit the field entirely —
      // treat absent exactly like unknown.
      region:
        typeof region === "string" && region.length > 0
          ? region.toUpperCase()
          : null,
    };
  } catch {
    // Network error, abort, or JSON parse failure. All collapse to "no
    // geo" — the playlist fetch will proceed without &geo=.
    return UNRESOLVED;
  }
};
